<script setup lang="ts">
  import type { RouteLocationRaw } from "vue-router"

  defineOptions({ name: "AppButton" })

  const props = withDefaults(
    defineProps<{
      /** Button type attribute (only for <button>) */
      type?: "button" | "submit" | "reset"
      /** Route for NuxtLink (makes component render as link) */
      to?: RouteLocationRaw
      /** External link (makes component render as <a>) */
      href?: string
      /** Color variant */
      color?: "primary" | "white" | "green" | "gray" | "blue"
      /** Visual variant */
      variant?: "solid" | "soft" | "outline" | "ghost"
      /** Size preset */
      size?: "2xs" | "xs" | "sm" | "md" | "lg" | "xl"
      /** Square button (icon only, equal width/height) */
      square?: boolean
      /** Disabled state */
      disabled?: boolean
      /** Loading state (shows spinner, disables interaction) */
      loading?: boolean
      /** Full width button */
      block?: boolean
      /** Name attribute (only for <button>) */
      name?: string
      /** ID attribute */
      id?: string
    }>(),
    {
      type: "button",
      color: "primary",
      variant: "solid",
      size: "md",
      square: false,
      disabled: false,
      loading: false,
      block: false,
      to: undefined,
      href: undefined,
      name: undefined,
      id: undefined,
    },
  )

  // Compute the component to render
  const componentType = computed(() => {
    if (props.to) return resolveComponent("NuxtLink")
    if (props.href) return "a"
    return "button"
  })

  // Compute classes
  const classes = computed(() => [
    "app-button",
    `app-button--${props.color}`,
    `app-button--${props.variant}`,
    `app-button--${props.size}`,
    {
      "app-button--square": props.square,
      "app-button--loading": props.loading,
      "app-button--block": props.block,
    },
  ])

  // Compute attributes based on component type
  const componentAttrs = computed(() => {
    const base = {
      id: props.id,
      class: classes.value,
    }

    // Button-specific
    if (!props.to && !props.href) {
      return {
        ...base,
        type: props.type,
        disabled: props.disabled || props.loading,
        name: props.name,
      }
    }

    // Link-specific (NuxtLink)
    if (props.to) {
      return {
        ...base,
        to: props.to,
        // Disable pointer events if disabled
        style:
          props.disabled || props.loading
            ? { pointerEvents: "none", opacity: 0.5 }
            : undefined,
      }
    }

    // External link
    return {
      ...base,
      href: props.href,
      style:
        props.disabled || props.loading
          ? { pointerEvents: "none", opacity: 0.5 }
          : undefined,
    }
  })
</script>

<template>
  <component :is="componentType" v-bind="componentAttrs">
    <span v-if="loading" class="app-button__spinner" aria-hidden="true"></span>
    <span
      class="app-button__content"
      :class="{ 'app-button__content--hidden': loading }"
    >
      <slot name="leading" />
      <slot />
      <slot name="trailing" />
    </span>
  </component>
</template>

<style scoped>
  .app-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: "Inter", sans-serif;
    font-weight: 700;
    font-size: 0.8125rem;
    line-height: 1;
    text-align: center;
    cursor: pointer;
    background: linear-gradient(
      180deg,
      rgba(50, 50, 50, 1) 0%,
      rgba(39, 39, 39, 1) 100%
    );
    border: 1px solid var(--app-button-border, #000);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
    border-radius: var(--app-button-radius, 5px);
    padding: 0.9375rem;
    color: var(--app-button-color, #fff);
    transition: all 0.2s;
    white-space: nowrap;
  }

  .app-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Size variants - scale from base 15px (0.9375rem) padding, 13px (0.8125rem) font */
  .app-button--2xs {
    padding: 0.375rem 0.5rem;
    font-size: 0.625rem;
  }
  .app-button--xs {
    padding: 0.5rem 0.625rem;
    font-size: 0.6875rem;
  }
  .app-button--sm {
    padding: 0.75rem 0.875rem;
    font-size: 0.75rem;
  }
  .app-button--md {
    padding: 0.9375rem;
    font-size: 0.8125rem;
  }
  .app-button--lg {
    padding: 1.125rem 1.25rem;
    font-size: 0.875rem;
  }
  .app-button--xl {
    padding: 1.25rem 1.5rem;
    font-size: 1rem;
  }

  /* Square variant (icon buttons) */
  .app-button--square {
    width: 2rem;
    height: 2rem;
    padding: 0;
    justify-content: center;
  }
  .app-button--square.app-button--2xs {
    width: 1.5rem;
    height: 1.5rem;
  }
  .app-button--square.app-button--xs {
    width: 1.75rem;
    height: 1.75rem;
  }
  .app-button--square.app-button--sm {
    width: 2rem;
    height: 2rem;
  }
  .app-button--square.app-button--md {
    width: 2.25rem;
    height: 2.25rem;
  }
  .app-button--square.app-button--lg {
    width: 2.75rem;
    height: 2.75rem;
  }
  .app-button--square.app-button--xl {
    width: 3.5rem;
    height: 3.5rem;
  }

  /* Block (full width) */
  .app-button--block {
    width: 100%;
  }

  /* Hover state - inverted gradient, no blue outline */
  .app-button:hover:not(:disabled) {
    background: linear-gradient(
      180deg,
      rgba(39, 39, 39, 1) 0%,
      rgba(50, 50, 50, 1) 100%
    );
    outline: none;
  }

  .app-button:focus-visible {
    outline: none;
  }

  /* Color variants - primary is now white by default */
  .app-button--primary {
    color: var(--app-button-primary-color, #fff);
  }
  .app-button--white {
    color: var(--app-button-white-color, #fff);
  }
  .app-button--green {
    color: var(--app-button-green-color, #22c55e);
  }
  .app-button--gray {
    color: var(--app-button-gray-color, #9ca3af);
  }
  .app-button--blue {
    color: var(--app-button-blue-color, #fff);
    background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
    border-color: #1d4ed8;
  }
  .app-button--blue:hover:not(:disabled) {
    background: linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%);
    border-color: #2563eb;
  }

  /* Variant modifiers */
  .app-button--solid {
    /* Default is solid, no changes needed */
  }

  .app-button--soft {
    background: linear-gradient(
      180deg,
      rgba(50, 50, 50, 0.5) 0%,
      rgba(39, 39, 39, 0.5) 100%
    );
  }

  .app-button--outline {
    background: transparent;
    box-shadow: none;
  }
  .app-button--outline:hover:not(:disabled) {
    background: linear-gradient(
      180deg,
      rgba(39, 39, 39, 0.5) 0%,
      rgba(50, 50, 50, 0.5) 100%
    );
  }

  .app-button--ghost {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
  }
  .app-button--ghost:hover:not(:disabled) {
    background: linear-gradient(
      180deg,
      rgba(39, 39, 39, 0.5) 0%,
      rgba(50, 50, 50, 0.5) 100%
    );
    border-color: var(--app-button-border, #000);
  }

  /* Loading spinner */
  .app-button__spinner {
    position: absolute;
    width: 1em;
    height: 1em;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: app-button-spin 0.6s linear infinite;
  }

  @keyframes app-button-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .app-button__content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .app-button__content--hidden {
    opacity: 0;
  }
</style>
